﻿export class ContactName implements IContactName {
    constructor(contact?: IContactName | string) {
        if (typeof contact === "string") {
            const parts: string[] = this.splitNames(contact);
            this.salutation = parts[0];
            this.firstNames = parts[1];
            this.preferredName = parts[2];
            this.lastName = parts[3];
            this.suffix = parts[4];
        } else {
            // initialize typed object from generic object
            this.salutation = contact?.salutation;
            this.firstNames = contact?.firstNames;
            this.preferredName = contact?.preferredName;
            this.lastName = contact?.lastName;
            this.suffix = contact?.suffix;
            this.role = contact?.role;
        }
    }

    toJSON(): object {
        // return generic object with properties to stringify
        return {
            salutation: this.salutation || undefined,
            firstNames: this.firstNames || undefined,
            preferredName: this.preferredName || undefined,
            lastName: this.lastName || undefined,
            suffix: this.suffix || undefined,
            role: this.role || undefined
        };
    }

    salutation?: string;
    firstNames?: string;
    preferredName?: string;
    lastName?: string;
    suffix?: string;
    role?: string;

    // <summary>
    // Splits a person name into salutation, first names, last name, and suffix.
    // </summary>
    // <param name="fullname"></param>
    // <returns>Returns an array of 4 strings corresponding to salutation, first names, last name, and suffix.</returns>
    // <remarks>
    // The name is parsed using the following case-insensitive syntax:
    // [salutation] [first_names] ["preferred_name"] [last_name_prefix]* last_name [suffix]
    // where each item is a word separated with one or more spaces and
    // salutation is mr., master, mister, mrs., miss, ms., dr., rev., or fr.; 
    // last_name_prefix is al, bin, da, de, del, della, der, di, dos, du, el, la, le, mac, mc, saint, ter, van, von, or st.; and 
    // suffix is I, II, III, IV, V, senior, junior, phd, md, dmd, sr., or jr.
    // last_name_prefix and last_name are combined into a single string in the returned array.
    // The array order is salutation, first_names, preferred_name, last_name_prefix and last_name, suffix.
    // </remarks>
    private splitNames(fullname: string): string[] {
        if (fullname == null)
            return ["", "", "", "", ""];
        const rx =
            /^(?:\s*)(?:((?:mr|master|mister|mrs|miss|ms|dr|rev|fr)\.?)\s+)?(?:(.+?)\s+)??(?:"(.+?)"\s+)??((?:(?:al|bin|da|de|del|della|der|di|dos|du|el|la|le|mac|mc|saint|ter|van|von|(?:(?:st)\.?))\s+)*[^\s]+)(?:\s+(I|II|III|IV|V|senior|junior|phd|md|dmd|(?:(?:sr|jr)\.?)))?(?:\s*)$/i;
        const m = rx.exec(fullname);
        return m ? [m[1], m[2], m[3], m[4], m[5]] : ["", "", "", fullname, ""];
    }


    private join(separator: string, coalesce: boolean, values: string[]): string {
        let s = "";
        for (const v of values)
            if (v || !coalesce)
                s += (s.length > 0 ? separator || "" : "") + v;
        return s;
    }

    toString(): string {
        return this.join(" ",
            true,
            [
                this.salutation || "", this.firstNames || "", this.preferredName ? `"${this.preferredName}"` : "",
                this.lastName || "", this.suffix || ""
            ]);
    }
}

export interface IContactName {
    salutation?: string;
    firstNames?: string;
    preferredName?: string;
    lastName?: string;
    suffix?: string;
    role?: string;
}